import { Component, OnInit, Input, Output, EventEmitter, Inject, LOCALE_ID } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { getUserType, State } from "../../reducers";
import { SubscribingComponent } from "../../common";

import { Statement } from "../../statements";
import { RefundDialogComponent } from "../../statements/refund-dialog/refund-dialog.component";
import { PackageOptions } from "app/statements/package-options";
import { takeUntil } from "rxjs/operators";
import { StatementService } from "app/statements/statement.service";
import { formatDate } from "@angular/common";
import { FirebaseEventsService } from "app/common/firebase-events.service";
import { UserType } from "../../users/user-type";
import { ConfirmTaxReturnComponentPath } from "app/core/workflow/page-path/statement-page-path/confirm-tax-return-component.path";
import { IdentityVerificationComponentPath } from "app/core/workflow/page-path/statement-page-path/identity-verification-component.path";
import { StatementStatuses } from "app/modules/statement/models/statement/statement.statuses";
import { StatementProgressPipe } from "app/shared/pipes/statement-progress.pipe";
import { DownloadFileDialogComponent } from "app/common/decision-dialog/download-file-dialog.component";
import {
  NewMessageDialogComponent,
  MESSAGE_DOCUMENT_TYPE,
  ATTACHMENT_SUBTYPE
} from "app/common/send-message-dialog/send-message-dialog.component";
import { MessageSenderHttpService } from "app/common/send-message-dialog/http-service/message-sender.http.service";
import { DocumentRequestDialogComponent } from "app/dashboard/document-request-dialog/document-request-dialog.component";

@Component({
  selector: "app-card-view-item",
  templateUrl: "./card-view-item.component.html",
  styleUrls: ["./card-view-item.component.scss"],
  standalone: false
})
export class CardViewItemComponent extends SubscribingComponent implements OnInit {
  private readonly rabeSupportedFromYear = 2023;
  @Input() statement: Statement;
  @Input() hideEditing = false;
  @Input() hideRemoving = false;
  @Input() hideInvoice = false;
  @Input() hideCloning = false;
  @Input() hidePdf = false;
  @Output() deleteStatement = new EventEmitter<void>();
  @Output() downloadPdf = new EventEmitter<void>();
  @Output() downloadInvoice = new EventEmitter<void>();
  @Output() showDownloadDialog = new EventEmitter<void>();
  @Output() cloneStatement = new EventEmitter<void>();
  public isTaxAdviser = false;
  public isBusiness = false;
  public userType: string;
  public steuerSendDate = "";
  public statementStatuses = StatementStatuses;
  public numberOfFilesToDownload = 0;

  public showHamburgerMenu = false;

  public ewrCount = 0;
  public rabeAttachmentsCount = 0;
  private readonly statementProgressPipe = new StatementProgressPipe();
  constructor(
    private dialog: MatDialog,
    private store: Store<State>,
    private router: Router,
    private statementService: StatementService,
    @Inject(LOCALE_ID) private locale: string,
    private firebaseEventsService: FirebaseEventsService,
    public messageSenderHttpService: MessageSenderHttpService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(getUserType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userType: string) => {
        if (userType) {
          this.isTaxAdviser = userType === "tax_adviser";
          this.isBusiness = userType === "business";
          this.userType = userType;
        }
      });

    if (
      this.userType === UserType.individual &&
      this.statement.serviceType === PackageOptions.tax_adviser &&
      !this.statement.isSent()
    ) {
      this.hidePdf = true;
    }

    if (!this.hideInvoice) {
      this.hideInvoice = !this.statement.canDownloadInvoice;
    }

    if (
      this.userType === UserType.individual &&
      this.statement.serviceType === PackageOptions.tax_adviser &&
      this.statement.statusPaidReady()
    ) {
      const sendDate = new Date();
      if (this.statement.taxAdviserWillSendInDays) {
        this.steuerSendDate = formatDate(
          sendDate.setDate(
            new Date(this.statement.onfidoCompletedDate).getDate() +
              parseInt(this.statement.taxAdviserWillSendInDays, 10)
          ),
          "dd/MM/yyyy",
          this.locale
        );
      } else {
        this.statementService
          .getConstantTaxAdviserWillSendInDays()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(
            (response) => {
              this.steuerSendDate = formatDate(
                sendDate.setDate(
                  new Date(this.statement.onfidoCompletedDate).getDate() +
                    parseInt(response["tax_adviser_will_send_in_days"], 10)
                ),
                "dd/MM/yyyy",
                this.locale
              );
            },
            (error) => {
              console.log("constant does not exist");
            }
          );
      }
    }

    this.countEwrAttachmentList();
  }

  openRefundDetailsDialog() {
    const dialogRef = this.dialog.open(RefundDialogComponent);
    dialogRef.componentInstance.statement = this.statement;
  }

  emitDownloadPdf() {
    this.downloadPdf.emit();
  }

  emitDownloadInvoice() {
    this.downloadInvoice.emit();
  }

  emitShowDownloadDialog() {
    this.showDownloadDialog.emit();
  }

  emitDeleteStatement() {
    this.firebaseEventsService.logEvent("click_delete_tax_card", null, this.statement.id);
    this.deleteStatement.emit();
  }

  editStatement() {
    if (!this.isTaxAdviser) {
      let urlPath;
      const employerId =
        this.statement.employers && this.statement.employers.length ? this.statement.employers[0].id : 0;
      console.log("shouldContinueIdentityVerification", this.shouldContinueIdentityVerification);

      if (this.shouldContinueIdentityVerification) {
        this.router.navigate([IdentityVerificationComponentPath.fullUrl(this.statement.id)]);
        return;
      }
      if (this.statement.isStandardPackage) {
        if (this.isBusiness) {
          urlPath = this.statement.isPaid ? "thank-you" : `employer/${employerId}`;
        } else {
          urlPath = this.statement.isPaid ? "thank-you" : `b2c/tax-card`;
        }
      } else if (this.statement.isPremiumPackage) {
        if (this.statement.isForClientApproval) {
          this.router.navigate([ConfirmTaxReturnComponentPath.fullUrl(this.statement.id)]);
          return;
        }
        if (this.statement.isPaid) {
          if (
            (this.userType === "individual" && !this.statement.identifikationsnummer) ||
            (this.statement.isSpouse() && !this.statement.spouse.identifikationsnummer)
          ) {
            urlPath = "tax-advisor-additional-validation";
          } else if (this.statement.hasVerifiedIdentity === null && !this.statement.vollmachtSigned) {
            urlPath = "vollmacht-authorization";
          } else if (this.statement.hasVerifiedIdentity === null && this.statement.vollmachtSigned) {
            urlPath = "thank-you";
          }
        } else {
          if (this.isBusiness) {
            urlPath = `employer/${employerId}`;
          } else {
            urlPath = `b2c/tax-card`;
          }
        }
      }

      const url = `new/statement/${this.statement.id}/${urlPath}`;
      this.router.navigate([url]);
    } else {
      const employerId =
        this.statement.employers && this.statement.employers.length ? this.statement.employers[0].id : 0;
      let urlPath = "";
      if (this.isBusiness) {
        urlPath = this.statement.isPaid ? `employer/${employerId}` : "dashboard";
        console.log("isBusiness");
      } else {
        // uploading the tax card or supplementing the ID number
        if (
          !this.statement.identifikationsnummer ||
          (this.statement.isSpouse() && !this.statement.spouse.identifikationsnummer)
        ) {
          urlPath = "tax-advisor-additional-validation";
          console.log("tax-advisor-additional-validation");
        } else {
          urlPath = this.statement.isPaid ? `b2c/tax-card` : "dashboard";
          console.log("employerbc");
        }
      }
      const url = `new/statement/${this.statement.id}/${urlPath}`;
      this.router.navigate([url]);
    }
  }

  // TAX-3408: a Sumsub attempt already exists for this paid statement, so the user resumes it
  // instead of landing on thank-you. `hasVerifiedIdentity === false` without a local attempt is
  // an old Onfido rejection and keeps the previous route.
  public get shouldContinueIdentityVerification(): boolean {
    return (
      this.statement.status === StatementStatuses.PAID &&
      this.statement.identityVerificationStarted === true &&
      this.statement.hasVerifiedIdentity !== true
    );
  }

  public getDashboardStatus() {
    return `DASHBOARD.STATUSES.${this.statement.dashboardStatus}`;
  }

  public getDashboardFinishPrecent() {
    return this.statementProgressPipe.transform(this.statement);
  }

  public emitCloneStatment() {
    this.firebaseEventsService.logEvent("click_clone_tax_card", null, this.statement.id);
    this.cloneStatement.emit();
  }

  public displayDownloadsButton(): boolean {
    const currentView = this.router.url.split("/").pop();

    switch (currentView) {
      case "thank-you":
        return this.statement.isPaid || this.statement.statusPaidReady();
      default:
        return (
          this.statement.isSent() ||
          this.statement.isApproved ||
          this.statement.isForClientApproval ||
          this.statement.isReportedErrors
        );
    }
  }

  public openAppealDecisionDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-new-message-dialog",
      data: {
        statementData: this.statement,
        titleMaxLength: 99,
        textMaxLength: 1500,
        messageType: MESSAGE_DOCUMENT_TYPE.APPEAL,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.APPEAL.MODAL_TITLE",
          titleLabel: "SEND_MESSAGE_DIALOG.APPEAL.TITLE_LABEL",
          messageLabel: "SEND_MESSAGE_DIALOG.APPEAL.MESSAGE_LABEL",
          addFileButton: "SEND_MESSAGE_DIALOG.APPEAL.ADD_FILE_BUTTON",
          checkboxText: "SEND_MESSAGE_DIALOG.APPEAL.CHECKBOX_TEXT",
          titleTooltip: "SEND_MESSAGE_DIALOG.APPEAL.TOOLTIP.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.APPEAL.TOOLTIP.MESSAGE"
        }
      }
    });
  }

  public openSendNewMessageDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-new-message-dialog",
      data: {
        statementData: this.statement,
        titleMaxLength: 99,
        textMaxLength: 1500,
        messageType: MESSAGE_DOCUMENT_TYPE.MESSAGE,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.MESSAGE.MODAL_TITLE",
          titleLabel: "SEND_MESSAGE_DIALOG.MESSAGE.TITLE_LABEL",
          messageLabel: "SEND_MESSAGE_DIALOG.MESSAGE.MESSAGE_LABEL",
          addFileButton: "SEND_MESSAGE_DIALOG.MESSAGE.ADD_FILE_BUTTON",
          titleTooltip: "SEND_MESSAGE_DIALOG.MESSAGE.TOOLTIP.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.MESSAGE.TOOLTIP.MESSAGE"
        }
      }
    });
  }

  public openSendAttachmentDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: null,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.ATTACHMENT.MODAL_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON",
          titleTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.MESSAGE"
        }
      }
    });
  }

  public openDocumentRequestDialog(): void {
    this.dialog.open(DocumentRequestDialogComponent, {
      panelClass: "document-request-dialog",
      data: { statement: this.statement }
    });
  }

  public openSendRabeDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.RABE,
        text: {
          modalTitle: "SEND_MESSAGE_DIALOG.RABE.MODAL_TITLE",
          addFileButton: "SEND_MESSAGE_DIALOG.RABE.ADD_FILE_BUTTON",
          selectBox: {
            title: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.TITLE",
            label: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.LABEL",
            placeholder: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.PLACEHOLDER"
          },
          onResponse: {
            successTitle: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_RABE.TITLE",
            successMessage: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_RABE.REQUEST"
          }
        }
      }
    });
  }

  public openRabeAttachmentListDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.RABE,
        previewOnlyRabeMode: true,
        text: {
          modalTitle: "DASHBOARD.ATTACHMENT.TITLE",
          selectBox: {
            title: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.TITLE",
            label: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.LABEL",
            placeholder: "SEND_MESSAGE_DIALOG.RABE.SELECT_BOX.PLACEHOLDER"
          }
        }
      }
    });
  }
  public openSendEwrDialogComponent() {
    this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: ATTACHMENT_SUBTYPE.EWR,
        text: {
          modalTitle: "COMMON.SEND_EWR",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON",
          titleTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_EWR.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.SEND_EWR.MESSAGE"
        }
      }
    });
  }

  public openSaveEwrDialogComponent() {
    const dialogRef: MatDialogRef<NewMessageDialogComponent, any> = this.dialog.open(NewMessageDialogComponent, {
      panelClass: "send-attachment-dialog",
      data: {
        statementData: this.statement,
        messageType: MESSAGE_DOCUMENT_TYPE.ATTACHMENT,
        subtype: ATTACHMENT_SUBTYPE.EWR,
        onlySave: true,
        text: {
          modalTitle: "COMMON.ATTACH_EWR",
          addFileButton: "SEND_MESSAGE_DIALOG.ATTACHMENT.ADD_FILE_BUTTON",
          titleTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.ATTACH_EWR.TITLE",
          messageTooltip: "SEND_MESSAGE_DIALOG.ATTACHMENT.TOOLTIP.ATTACH_EWR.MESSAGE",
          onResponse: {
            successTitle: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_EWR.TITLE",
            successMessage: "SEND_MESSAGE_DIALOG.SUCCESS.SEND_EWR.REQUEST"
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(({ dataSaved }) => {
      if (dataSaved) this.countEwrAttachmentList();
    });
  }

  public get shouldShowLegacyEwrAttachmentButton(): boolean {
    return !this.isTaxAdviser && !this.isBusiness && !this.statement.isSent() && this.isLegacyEwrStatement;
  }

  public get shouldShowHamburgerMenu(): boolean {
    return this.statement.isSent() || this.shouldShowLegacyEwrAttachmentButton;
  }

  public get isRabeEnabledForStatement(): boolean {
    const statementYear = Number(this.statement?.fullYear);
    if (!Number.isFinite(statementYear)) return true;

    return statementYear >= this.rabeSupportedFromYear;
  }

  private get isLegacyEwrStatement(): boolean {
    const statementYear = Number(this.statement?.fullYear);
    if (!Number.isFinite(statementYear)) return false;

    return statementYear < this.rabeSupportedFromYear;
  }

  public countEwrAttachmentList() {
    this.ewrCount = 0;
    this.rabeAttachmentsCount = 0;

    this.messageSenderHttpService.getMessageAttachmentList(this.statement.id).subscribe({
      next: (response: any) => {
        const attachmentMessages = Array.isArray(response) ? response : [];

        const rabeMessages = attachmentMessages.filter(
          (attachment) => String(attachment?.type || "").toLowerCase() === MESSAGE_DOCUMENT_TYPE.RABE.toLowerCase()
        );
        this.rabeAttachmentsCount = rabeMessages.reduce((count, attachment) => {
          return count + (Array.isArray(attachment?.files) ? attachment.files.length : 0);
        }, 0);

        const filteredAttachments = attachmentMessages.filter((attachment) =>
          [ATTACHMENT_SUBTYPE.EWR].includes(attachment.subtype)
        );
        filteredAttachments.forEach((attachment) => {
          if (attachment?.files?.length === 0) return;

          this.ewrCount += attachment.files.length;
        });
      },
      error: (error) => {
        this.rabeAttachmentsCount = 0;
        console.error(error);
      }
    });
  }
}
